import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Clock, LifeBuoy, MessageCircle, Plus, Sparkles } from "lucide-react";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { EmptyState } from "@shared/ui/feedback/EmptyState";
import { Card } from "@shared/ui/containers/Card";
import { Button } from "@shared/ui/primitives/Button";
import { Fab } from "@shared/ui/primitives/Fab";
import { formatDateTime } from "@shared/lib/format";
import { pageCopy } from "@shared/lib/pageCopy";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { cn } from "@shared/lib/cn";
import { fetchSupportRequests, type SupportRequest } from "./api";

const STATUS: Record<string, { label: string; color: string }> = {
  open: { label: "Open", color: "var(--warning)" },
  in_progress: { label: "In progress", color: "var(--info)" },
  awaiting_member: { label: "Awaiting you", color: "var(--warning)" },
  resolved: { label: "Resolved", color: "var(--success)" },
  closed: { label: "Closed", color: "var(--ink-muted)" },
};

type FilterKey = "open" | "inProgress" | "resolved";

/** Status groups used by the colored filter tiles. */
const FILTERS: ReadonlyArray<{ key: FilterKey; label: string; color: string }> = [
  { key: "open", label: "Open", color: "var(--warning)" },
  { key: "inProgress", label: "In progress", color: "var(--info)" },
  { key: "resolved", label: "Resolved", color: "var(--success)" },
];

function matchesFilter(status: SupportRequest["status"], key: FilterKey): boolean {
  if (key === "open") return status === "open";
  if (key === "inProgress") return status === "in_progress" || status === "awaiting_member";
  return status === "resolved" || status === "closed";
}

/**
 * Support — "My requests": a dark estate header over a feed of request cards.
 * The colored stat tiles double as status filters; tapping a tile filters the
 * list, tapping it again clears the filter.
 */
export function SupportList(): JSX.Element {
  const copy = pageCopy().support.list;
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterKey | null>(null);
  const query = useQuery({
    queryKey: ["support-requests"],
    queryFn: fetchSupportRequests,
  });
  const requests = query.data ?? [];

  const countFor = (key: FilterKey) =>
    requests.filter((r) => matchesFilter(r.status, key)).length;

  const visible = filter ? requests.filter((r) => matchesFilter(r.status, filter)) : requests;
  const activeLabel = FILTERS.find((f) => f.key === filter)?.label.toLowerCase();

  return (
    <div className="min-h-full bg-paper">
      {/* ── Header ───────────────────────────────────────────────── */}
      <HeroHeader
        collapsible
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
      />

      {/* ── Live Chat CTA ────────────────────────────────────────── */}
      <div className="px-5 pt-5">
        <button
          type="button"
          onClick={() => navigate("/app/support/chat")}
          className="flex w-full items-center gap-3 rounded-2xl bg-gradient-to-br from-[var(--primary-600)] to-[var(--primary-800)] px-4 py-3.5 text-left text-white shadow-sm active:scale-[0.99]"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-white/15">
            <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5 font-semibold">
              Live Chat
              <span className="inline-flex items-center gap-0.5 rounded-pill bg-white/20 px-1.5 py-0.5 text-[0.5625rem] font-bold uppercase tracking-wide">
                <Sparkles className="h-2.5 w-2.5" strokeWidth={2} />
                AI
              </span>
            </span>
            <span className="block text-body-sm text-white/80">Chat with our assistant — instant help</span>
          </span>
          <ChevronRight className="h-5 w-5 shrink-0 text-white/80" strokeWidth={2} />
        </button>
      </div>

      {/* ── Filter tiles ─────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-3 px-5 pt-4">
        {FILTERS.map((f) => (
          <FilterTile
            key={f.key}
            label={f.label}
            color={f.color}
            value={countFor(f.key)}
            active={filter === f.key}
            onClick={() => setFilter((prev) => (prev === f.key ? null : f.key))}
          />
        ))}
      </div>

      {/* ── Requests ─────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 px-5 pt-5 pb-28">
        {query.isLoading ? (
          <>
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </>
        ) : query.isError ? (
          <ErrorState onRetry={() => query.refetch()} />
        ) : requests.length === 0 ? (
          <EmptyState
            icon={<LifeBuoy className="h-7 w-7" strokeWidth={1.5} />}
            title="No requests yet."
            description="Anything that needs the estate team — log it here."
          />
        ) : visible.length === 0 ? (
          <EmptyState
            icon={<LifeBuoy className="h-7 w-7" strokeWidth={1.5} />}
            title={`No ${activeLabel} requests.`}
            description="Tap the filter again to see everything."
            action={<Button variant="secondary" onClick={() => setFilter(null)}>Clear filter</Button>}
          />
        ) : (
          visible.map((request) => (
            <RequestCard
              key={request.request_id}
              request={request}
              onClick={() =>
                navigate(`/app/support/requests/${request.request_id}`)
              }
            />
          ))
        )}
      </div>

      <Fab onClick={() => navigate("/app/support/requests/new")} label="New request">
        <Plus className="h-6 w-6" strokeWidth={2} />
      </Fab>
    </div>
  );
}

function FilterTile({
  value,
  label,
  color,
  active,
  onClick,
}: {
  value: number;
  label: string;
  color: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        // color-mix gives a reliable light tint of the semantic colour (the
        // `var(--x)/opacity` Tailwind form doesn't compile for these tokens).
        backgroundColor: `color-mix(in oklab, ${color} ${active ? "24%" : "12%"}, var(--surface-card))`,
        borderColor: active ? color : "transparent",
      }}
      className={cn(
        "rounded-xl border px-3 py-3.5 text-left transition-colors duration-fast",
        "active:scale-[0.99]",
      )}
    >
      <p className="font-display text-display-lg" style={{ color }}>
        {value}
      </p>
      <p className="text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-ink-secondary">
        {label}
      </p>
    </button>
  );
}

function RequestCard({
  request,
  onClick,
}: {
  request: SupportRequest;
  onClick: () => void;
}): JSX.Element {
  const status = STATUS[request.status] ?? {
    label: request.status,
    color: "var(--ink-secondary)",
  };
  return (
    <Card
      asChild
      noGrain
      className="rounded-xl p-4 text-left transition-transform duration-fast active:scale-[0.99]"
    >
      <button type="button" onClick={onClick} className="w-full">
        <div className="flex items-start justify-between gap-3">
          <span className="text-caption uppercase tracking-[0.06em] text-ink-muted">
            {[request.reference, request.topic_name]
              .filter(Boolean)
              .join(" · ")}
          </span>
          <span
            style={{
              backgroundColor: `color-mix(in oklab, ${status.color} 16%, var(--surface-card))`,
              color: status.color,
            }}
            className="shrink-0 rounded-pill px-2.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide"
          >
            {status.label}
          </span>
        </div>
        <p className="mt-1 font-display text-headline leading-tight text-ink">
          {request.subject}
        </p>
        {request.last_message_excerpt ? (
          <p className="mt-1 line-clamp-2 text-body-sm text-ink-secondary">
            {request.last_message_excerpt}
          </p>
        ) : null}
        <p className="mt-2 flex items-center gap-1.5 text-caption text-ink-muted">
          <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />
          {formatDateTime(request.updated_at ?? request.created_at) || "—"}
        </p>
      </button>
    </Card>
  );
}
